import { FunctionComponent, useState, useCallback, useEffect } from 'react';
import { IconButton, TextField } from '../../tailwindcomponents';
import { Edit, Delete, Save, Close, InfoOutlined } from '../../components/icons';
import tasksApi from '../../api/tasks';
import { TaskNote } from '../../types/tasks';

type Props = {
    taskBarcode: string;
    taskName: string;
    notes: TaskNote[];
    currentUserId: number;
    onNotesUpdated: (notes: TaskNote[]) => void;
};

type BannerState = {
    message: string;
    action?: 'confirm-delete';
    noteId?: number;
} | null;

const TaskNotesManager: FunctionComponent<Props> = ({
    taskBarcode,
    notes,
    currentUserId,
    onNotesUpdated,
}) => {
    const [newNoteText, setNewNoteText] = useState('');
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [editingText, setEditingText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [banner, setBanner] = useState<BannerState>(null);

    useEffect(() => {
        if (!banner || banner.action === 'confirm-delete') {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setBanner(null);
        }, 3500);

        return () => window.clearTimeout(timeoutId);
    }, [banner]);

    const showBanner = (message: string) => {
        setBanner({ message });
    };

    const handleCreateNote = useCallback(async () => {
        if (!newNoteText.trim()) {
            showBanner('Please enter a note before saving.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await tasksApi.CreateTaskNote(taskBarcode, newNoteText.trim());
            if (response.status) {
                setNewNoteText('');
                const updatedNotes = await tasksApi.GetTaskNotes(taskBarcode);
                if (updatedNotes.status) {
                    onNotesUpdated(updatedNotes.data);
                    showBanner('Note created successfully.');
                } else {
                    showBanner(updatedNotes.message || 'Note was created, but the list could not be refreshed.');
                }
            } else {
                showBanner(response.message || 'Failed to create note.');
            }
        } catch (error: any) {
            showBanner(error.message || 'Error creating note.');
        } finally {
            setIsLoading(false);
        }
    }, [newNoteText, taskBarcode, onNotesUpdated]);

    const handleEditNote = useCallback(async (noteId: number) => {
        if (!editingText.trim()) {
            showBanner('Please enter a note before saving.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await tasksApi.EditTaskNote(noteId, editingText.trim());
            if (response.status) {
                setEditingNoteId(null);
                setEditingText('');
                const updatedNotes = await tasksApi.GetTaskNotes(taskBarcode);
                if (updatedNotes.status) {
                    onNotesUpdated(updatedNotes.data);
                    showBanner('Note updated successfully.');
                } else {
                    showBanner(updatedNotes.message || 'Note was updated, but the list could not be refreshed.');
                }
            } else {
                showBanner(response.message || 'Failed to update note.');
            }
        } catch (error: any) {
            showBanner(error.message || 'Error updating note.');
        } finally {
            setIsLoading(false);
        }
    }, [editingText, taskBarcode, onNotesUpdated]);

    const requestDeleteNote = (noteId: number) => {
        setBanner({
            message: 'Delete this note? This will remove it from the current remarks view.',
            action: 'confirm-delete',
            noteId,
        });
    };

    const handleDeleteNote = useCallback(async (noteId: number) => {
        setIsLoading(true);
        try {
            const response = await tasksApi.DeleteTaskNote(noteId);
            if (response.status) {
                const updatedNotes = await tasksApi.GetTaskNotes(taskBarcode);
                if (updatedNotes.status) {
                    onNotesUpdated(updatedNotes.data);
                    showBanner('Note deleted successfully.');
                } else {
                    showBanner(updatedNotes.message || 'Note was deleted, but the list could not be refreshed.');
                }
            } else {
                showBanner(response.message || 'Failed to delete note.');
            }
        } catch (error: any) {
            showBanner(error.message || 'Error deleting note.');
        } finally {
            setBanner(null);
            setIsLoading(false);
        }
    }, [taskBarcode, onNotesUpdated]);

    const canEditNote = (note: TaskNote) => note.created_by_employee_id === currentUserId;
    
    // Filter to get only current versions, ordered by created_date descending
    const currentVersions = notes
        .filter(n => n.current_version && !n.is_deleted)
        .sort((a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime());

    return (
        <div className="flex flex-col gap-4 p-4">
            <div>
                <h3 className="font-semibold mb-2 text-lg">Task Notes</h3>
            </div>

            <div>
                <div
                    className={[
                        'overflow-hidden transition-all duration-300 ease-out',
                        banner ? 'max-h-32 opacity-100 translate-y-0' : 'max-h-0 opacity-0 -translate-y-3 pointer-events-none',
                    ].join(' ')}
                >
                    <div className="flex items-start justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sky-900 shadow-sm">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 rounded-full bg-sky-100 p-1 text-sky-700">
                                <InfoOutlined fontSize="small" />
                            </div>
                            <div>
                                <p className="m-0 text-sm font-semibold">Task Notes</p>
                                <p className="m-0 text-sm leading-5">{banner?.message}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {banner?.action === 'confirm-delete' && banner.noteId !== undefined && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteNote(banner.noteId!)}
                                        disabled={isLoading}
                                        className="rounded-md bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-sky-800 disabled:cursor-not-allowed disabled:bg-sky-400"
                                    >
                                        Delete
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setBanner(null)}
                                        disabled={isLoading}
                                        className="rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold text-sky-800 transition hover:bg-sky-100 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Existing Notes Display */}
            {currentVersions.length > 0 && (
                <div>
                    <div className="space-y-3 max-h-96 overflow-y-auto border border-line rounded p-3 bg-background">
                        {currentVersions.map((note) => (
                            <div key={note.comment_id} className="bg-background-paper p-3 rounded border border-gray-100">
                                {editingNoteId === note.comment_id ? (
                                    <div className="flex flex-col gap-2">
                                        <div>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                value={editingText}
                                                onChange={(e) => setEditingText(e.target.value)}
                                                placeholder="Edit note..."
                                                disabled={isLoading}
                                            />
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditNote(note.comment_id)}
                                                disabled={isLoading}
                                                color="primary"
                                            >
                                                <Save fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setEditingNoteId(null);
                                                    setEditingText('');
                                                }}
                                                disabled={isLoading}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex flex-wrap justify-between items-start">
                                            <div>
                                                <span className="text-xs font-semibold text-gray-600">
                                                    {note.created_by_employee_name}
                                                </span>
                                                <span className="text-xs text-gray-500 ml-2">
                                                    {note.created_date_text}
                                                </span>
                                                {note.version_id > 1 && (
                                                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded ml-2 inline-block">
                                                        Edited (v{note.version_id})
                                                    </span>
                                                )}
                                            </div>
                                            {canEditNote(note) && (
                                                <div>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setEditingNoteId(note.comment_id);
                                                            setEditingText(note.text);
                                                        }}
                                                        disabled={isLoading}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => requestDeleteNote(note.comment_id)}
                                                        disabled={isLoading}
                                                        color="error"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm m-0 whitespace-pre-wrap break-words">
                                                {note.text}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {currentVersions.length === 0 && (
                <div>
                    <span className="text-sm text-gray-400 italic">No notes yet</span>
                </div>
            )}

            {/* New Note Input */}
            <div>
                <div className="flex flex-col gap-2">
                    <div>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            placeholder="Add a new note..."
                            disabled={isLoading}
                        />
                    </div>
                    <div>
                        <IconButton
                            onClick={handleCreateNote}
                            disabled={isLoading || !newNoteText.trim()}
                            color="primary"
                            size="small"
                            className="gap-1"
                        >
                            <Save fontSize="small" /> Add Note
                        </IconButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskNotesManager;
